/** Formats an OTP resend duration as MM:SS (for example, 120 → 02:00). */
export function formatOtpResendCountdown(seconds: number): string {
  const safeSeconds =
    Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
}

/** True when CMS omits whitespace after `{resendTimer}` (e.g. `{resendTimer}ثانية`). */
export function otpResendTimerSuffixNeedsLeadingSpace(after: string): boolean {
  return after.length > 0 && !/^\s/.test(after);
}

/** Adds a visible gap only when the suffix is glued to the placeholder. */
export function normalizeOtpResendTimerSuffix(after: string): string {
  if (!otpResendTimerSuffixNeedsLeadingSpace(after)) {
    return after;
  }
  return `\u00a0\u00a0${after}`;
}

/**
 * LTR-isolated countdown for mixed RTL/LTR copy. Extra trailing spaces only when
 * the suffix has no leading whitespace (Arabic glued to timer).
 */
export function formatOtpResendCountdownForInlineCopy(
  seconds: number,
  suffixNeedsLeadingSpace = false,
): string {
  const countdown = formatOtpResendCountdown(seconds);
  const core = `\u2066${countdown}\u2069`;
  if (!suffixNeedsLeadingSpace) {
    return core;
  }
  return `${core}\u00a0\u00a0`;
}

export function splitOtpResendTimerTemplate(template: string): {
  before: string;
  after: string;
  suffixNeedsLeadingSpace: boolean;
} {
  const [before = '', after = ''] = template.split('{resendTimer}');
  const needsPad = otpResendTimerSuffixNeedsLeadingSpace(after);
  return {
    before,
    after: normalizeOtpResendTimerSuffix(after),
    suffixNeedsLeadingSpace: needsPad,
  };
}

export function insertOtpResendCountdownInTemplate(
  template: string,
  seconds: number,
): string {
  if (!template.includes('{resendTimer}')) {
    return template;
  }
  const [before, ...rest] = template.split('{resendTimer}');
  const rawAfter = rest.join('{resendTimer}');
  const needsPad = otpResendTimerSuffixNeedsLeadingSpace(rawAfter);
  const after = normalizeOtpResendTimerSuffix(rawAfter);
  return `${before}${formatOtpResendCountdownForInlineCopy(seconds, needsPad)}${after}`;
}
